import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Calendar as CalendarIcon, Clock, Truck, MapPin, 
  User, Phone, Mail, CheckCircle, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import VideoHero from "@/components/VideoHero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  { value: "08:00", label: "8:00 AM" },
  { value: "09:00", label: "9:00 AM" },
  { value: "10:00", label: "10:00 AM" },
  { value: "11:00", label: "11:00 AM" },
  { value: "12:00", label: "12:00 PM" },
  { value: "13:00", label: "1:00 PM" },
  { value: "14:00", label: "2:00 PM" },
  { value: "15:00", label: "3:00 PM" },
  { value: "16:00", label: "4:00 PM" },
  { value: "17:00", label: "5:00 PM" },
];

const DURATION_OPTIONS = [
  { value: "2", label: "2 hours (small move)" },
  { value: "3", label: "3 hours" },
  { value: "4", label: "4 hours (medium move)" },
  { value: "5", label: "5 hours" },
  { value: "6", label: "6 hours (large move)" },
  { value: "8", label: "8 hours (full day)" },
];

const BookingPage = () => {
  const [date, setDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [availableSlots, setAvailableSlots] = useState(TIME_SLOTS.map(t => t.value));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    pickup_address: "",
    delivery_address: "",
    booking_time: "",
    estimated_hours: "4",
    notes: "",
    items_summary: ""
  });

  // Fetch available slots when date changes
  useEffect(() => {
    const fetchSlots = async () => {
      if (date) {
        try {
          const dateStr = format(date, "yyyy-MM-dd");
          const response = await axios.get(`${API}/available-slots/${dateStr}`);
          setAvailableSlots(response.data.available_slots);
        } catch (error) {
          console.error("Error fetching slots:", error);
          setAvailableSlots(TIME_SLOTS.map(t => t.value));
        }
      }
    };
    fetchSlots();
  }, [date]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !formData.booking_time) {
      toast.error("Please select a date and time");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...formData,
      booking_date: format(date, "yyyy-MM-dd"),
      estimated_hours: parseFloat(formData.estimated_hours)
    };

    try {
      const response = await axios.post(`${API}/bookings`, payload);
      setBookingConfirmed(response.data);
      toast.success("Booking confirmed! We'll be in touch soon.");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Failed to create booking. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Disable past dates and weekends (optional)
  const disabledDays = (day) => {
    return day < new Date(new Date().setHours(0, 0, 0, 0));
  };

  if (bookingConfirmed) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="booking-confirmed">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center">
            <div className="bg-green-50 border border-green-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h1 className="text-3xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-4 uppercase">
              Booking Confirmed!
            </h1>
            
            <p className="text-lg text-slate-600 mb-8">
              Thank you for booking with Tony Moves It. We'll contact you shortly to confirm the details.
            </p>

            <div className="bg-[#0047AB] text-white rounded-xl p-6 mb-8 text-left">
              <h3 className="font-bold text-lg mb-4">Booking Details:</h3>
              <div className="space-y-2">
                <p><strong>Confirmation ID:</strong> {bookingConfirmed.id.slice(0, 8).toUpperCase()}</p>
                <p><strong>Date:</strong> {format(new Date(bookingConfirmed.booking_date), "MMMM d, yyyy")}</p>
                <p><strong>Time:</strong> {TIME_SLOTS.find(t => t.value === bookingConfirmed.booking_time)?.label}</p>
                <p><strong>Duration:</strong> {bookingConfirmed.estimated_hours} hours</p>
                <p><strong>From:</strong> {bookingConfirmed.pickup_address}</p>
                <p><strong>To:</strong> {bookingConfirmed.delivery_address}</p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-8">
              <p className="text-slate-700">
                <strong>Next Steps:</strong> Tony will call or text you at{" "}
                <span className="text-[#0047AB]">{bookingConfirmed.customer_phone}</span> to confirm 
                your booking and provide a final quote.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => {
                  setBookingConfirmed(null);
                  setFormData({
                    customer_name: "",
                    customer_email: "",
                    customer_phone: "",
                    pickup_address: "",
                    delivery_address: "",
                    booking_time: "",
                    estimated_hours: "4",
                    notes: "",
                    items_summary: ""
                  });
                  setDate(null);
                }}
                variant="outline"
                className="font-bold"
                data-testid="new-booking-btn"
              >
                Make Another Booking
              </Button>
              <a href="tel:613-362-8180">
                <Button className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Tony: 613-362-8180
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page">
      {/* Hero Section with Video */}
      <VideoHero
        title="Book Your Move"
        subtitle="Select a date and time that works for you. We'll confirm your booking within 24 hours."
        minHeight="min-h-[30vh]"
      />

      <div className="bg-[#F8FAFC] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Date & Time */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-[#0047AB]" />
                Select Date & Time
              </h2>

              {/* Date Picker */}
              <div className="mb-6">
                <Label>Move Date *</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-2 h-12",
                        !date && "text-muted-foreground"
                      )}
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {date ? format(date, "MMMM d, yyyy") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(selectedDate) => {
                        setDate(selectedDate);
                        setCalendarOpen(false);
                      }}
                      disabled={disabledDays}
                      initialFocus
                      data-testid="calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Slot */}
              <div className="mb-6">
                <Label>Start Time *</Label>
                <Select 
                  value={formData.booking_time} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, booking_time: val }))}
                >
                  <SelectTrigger className="mt-2 h-12" data-testid="time-select">
                    <SelectValue placeholder="Select a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => (
                      <SelectItem 
                        key={slot.value} 
                        value={slot.value}
                        disabled={!availableSlots.includes(slot.value)}
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {slot.label}
                          {!availableSlots.includes(slot.value) && (
                            <span className="text-xs text-slate-400">(Booked)</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Duration */}
              <div>
                <Label>Estimated Duration *</Label>
                <Select 
                  value={formData.estimated_hours} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, estimated_hours: val }))}
                >
                  <SelectTrigger className="mt-2 h-12" data-testid="duration-select">
                    <SelectValue placeholder="How long do you need?" />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-500 mt-2">
                  Not sure? Get a <a href="/quote" className="text-[#0047AB] font-medium">free quote</a> first.
                </p>
              </div>
            </div>

            {/* Right Column - Contact & Address */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-xl font-bold font-['Barlow_Condensed'] text-slate-900 mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-[#0047AB]" />
                Your Information
              </h2>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="customer_name">Full Name *</Label>
                  <Input
                    id="customer_name"
                    name="customer_name"
                    placeholder="John Smith"
                    value={formData.customer_name}
                    onChange={handleInputChange}
                    className="mt-2"
                    data-testid="booking-name-input"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_email">Email *</Label>
                    <Input
                      id="customer_email"
                      name="customer_email"
                      type="email"
                      placeholder="john@email.com"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      className="mt-2"
                      data-testid="booking-email-input"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_phone">Phone *</Label>
                    <Input
                      id="customer_phone"
                      name="customer_phone"
                      type="tel"
                      placeholder="613-555-0123"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      className="mt-2"
                      data-testid="booking-phone-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="pickup_address">Pickup Address *</Label>
                  <Input
                    id="pickup_address"
                    name="pickup_address"
                    placeholder="123 Main St, Cornwall, ON"
                    value={formData.pickup_address}
                    onChange={handleInputChange}
                    className="mt-2"
                    data-testid="booking-pickup-input"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="delivery_address">Delivery Address *</Label>
                  <Input
                    id="delivery_address"
                    name="delivery_address"
                    placeholder="456 Oak Ave, Cornwall, ON"
                    value={formData.delivery_address}
                    onChange={handleInputChange}
                    className="mt-2"
                    data-testid="booking-delivery-input"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="items_summary">What are you moving?</Label>
                  <Textarea
                    id="items_summary"
                    name="items_summary"
                    placeholder="Brief summary: 2-bedroom apartment, couch, bed, dresser..."
                    value={formData.items_summary}
                    onChange={handleInputChange}
                    className="mt-2"
                    rows={2}
                    data-testid="booking-items-input"
                  />
                </div>

                <div>
                  <Label htmlFor="notes">Special Instructions</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    placeholder="Elevator access, stairs, parking info..."
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="mt-2"
                    rows={2}
                    data-testid="booking-notes-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <Button 
              type="submit"
              disabled={isSubmitting || !date || !formData.booking_time || !formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.pickup_address || !formData.delivery_address}
              className="bg-[#0047AB] hover:bg-[#003380] text-white font-bold text-lg py-6 px-12"
              data-testid="submit-booking"
            >
              {isSubmitting ? "Confirming..." : "Confirm Booking"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-slate-500 mt-4 text-sm">
              By booking, you agree to be contacted by Tony Moves It to confirm your reservation.
            </p>
          </div>
        </form>

        {/* Contact Alternative */}
        <div className="mt-12 bg-[#0047AB] rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold font-['Barlow_Condensed'] mb-4 uppercase">
            Prefer to Talk to Someone?
          </h3>
          <p className="text-white/90 mb-6">
            Call Tony directly to discuss your move and get personalized service.
          </p>
          <a href="tel:613-362-8180">
            <Button className="bg-white text-[#0047AB] hover:bg-slate-100 font-bold text-lg py-6 px-8">
              <Phone className="w-5 h-5 mr-2" />
              613-362-8180
            </Button>
          </a>
        </div>
      </div>
      </div>
    </div>
  );
};

export default BookingPage;
